import { isObject } from '@vue/shared'
import { cloneDeep, conforms } from 'lodash'
import { saveAs } from 'file-saver'
import { ElMessage } from 'element-plus'
import type { RouteLocationNormalized } from 'vue-router'
import Compressor from 'compressorjs'
import { smart } from '@/api/app'

/**
 * @description 添加单位
 * @param {String | Number} value 值 100
 * @param {String} unit 单位 px em rem
 */
export const addUnit = (value: string | number, unit = 'px') => {
    return !Object.is(Number(value), NaN) ? `${value}${unit}` : value
}

/**
 * @description 添加单位
 * @param {unknown} value
 * @return {Boolean}
 */
export const isEmpty = (value: unknown) => {
    return value == null && typeof value == 'undefined'
}

/**
 * @description 树转数组，队列实现广度优先遍历
 * @param {Array} data  数据
 * @param {Object} props `{ children: 'children' }`
 */

export const treeToArray = (data: any[], props = { children: 'children' }) => {
    data = cloneDeep(data)
    const { children } = props
    const newData = []
    const queue: any[] = []
    data.forEach((child: any) => queue.push(child))
    while (queue.length) {
        const item: any = queue.shift()
        if (item[children]) {
            item[children].forEach((child: any) => queue.push(child))
            delete item[children]
        }
        newData.push(item)
    }
    return newData
}

/**
 * @description 数组转
 * @param {Array} data  数据
 * @param {Object} props `{ parent: 'pid', children: 'children' }`
 */

export const arrayToTree = (
    data: any[],
    props = { id: 'id', parentId: 'pid', children: 'children' }
) => {
    data = cloneDeep(data)
    const { id, parentId, children } = props
    const result: any[] = []
    const map = new Map()
    data.forEach((item) => {
        map.set(item[id], item)
        const parent = map.get(item[parentId])
        if (parent) {
            parent[children] = parent[children] ?? []
            parent[children].push(item)
        } else {
            result.push(item)
        }
    })
    return result
}

/**
 * @description 获取正确的路经
 * @param {String} path  数据
 */
export function getNormalPath(path: string) {
    if (path.length === 0 || !path || path == 'undefined') {
        return path
    }
    const newPath = path.replace('//', '/')
    const length = newPath.length
    if (newPath[length - 1] === '/') {
        return newPath.slice(0, length - 1)
    }
    return newPath
}

/**
 * @description对象格式化为Query语法
 * @param { Object } params
 * @return {string} Query语法
 */
export function objectToQuery(params: Record<string, any>): string {
    let query = ''
    for (const props of Object.keys(params)) {
        const value = params[props]
        const part = encodeURIComponent(props) + '='
        if (!isEmpty(value)) {
            if (isObject(value)) {
                for (const key of Object.keys(value)) {
                    if (!isEmpty(value[key])) {
                        const params = props + '[' + key + ']'
                        const subPart = encodeURIComponent(params) + '='
                        query += subPart + encodeURIComponent(value[key]) + '&'
                    }
                }
            } else {
                query += part + encodeURIComponent(value) + '&'
            }
        }
    }
    return query.slice(0, -1)
}

/**
 * @description 时间格式化
 * @param dateTime { number } 时间戳
 * @param fmt { string } 时间格式
 * @return { string }
 */
// yyyy:mm:dd|yyyy:mm|yyyy年mm月dd日|yyyy年mm月dd日 hh时MM分等,可自定义组合
export const timeFormat = (dateTime: number, fmt = 'yyyy-mm-dd') => {
    // 如果为null,则格式化当前时间
    if (!dateTime) {
        dateTime = Number(new Date())
    }
    // 如果dateTime长度为10或者13，则为秒和毫秒的时间戳，如果超过13位，则为其他的时间格式
    if (dateTime.toString().length == 10) {
        dateTime *= 1000
    }
    const date = new Date(dateTime)
    let ret
    const opt: any = {
        'y+': date.getFullYear().toString(), // 年
        'm+': (date.getMonth() + 1).toString(), // 月
        'd+': date.getDate().toString(), // 日
        'h+': date.getHours().toString(), // 时
        'M+': date.getMinutes().toString(), // 分
        's+': date.getSeconds().toString() // 秒
    }
    for (const k in opt) {
        ret = new RegExp('(' + k + ')').exec(fmt)
        if (ret) {
            fmt = fmt.replace(
                ret[1],
                ret[1].length == 1
                    ? opt[k]
                    : opt[k].padStart(ret[1].length, '0')
            )
        }
    }
    return fmt
}

/**
 * @description 获取不重复的id
 * @param length { Number } id的长度
 * @return { String } id
 */
export const getNonDuplicateID = (length = 8) => {
    let idStr = Date.now().toString(36)
    idStr += Math.random().toString(36).substring(3, length)
    return idStr
}

/**
 * 滚动到指定元素
 */

export const scrollIntoView = (
    element: Element,
    behavior: ScrollBehavior = 'smooth'
): void => {
    element.scrollIntoView({ behavior })
}

/**
 * 对象转FormData
 */

export const objectToFormData = (
    obj: any,
    form = new FormData(),
    namespace = ''
) => {
    const fd = form || new FormData()
    let formKey
    for (const property in obj) {
        if (obj.hasOwnProperty(property)) {
            if (namespace) {
                formKey = namespace + '[' + property + ']'
            } else {
                formKey = property
            }

            // if the property is an object, but not a File,
            // use recursivity.
            if (
                typeof obj[property] === 'object' &&
                !(obj[property] instanceof File)
            ) {
                objectToFormData(obj[property], fd, formKey)
            } else {
                // if it's a string or a File object
                fd.append(formKey, obj[property])
            }
        }
    }
    return fd
}

/**
 * 链接提取文件名
 * @param url
 * @returns
 */
export const getFileName = (url: string): string => {
    if (!url) {
        ElMessage.error('路径不存在!')
        return ''
    }
    const fileNmae = url.split('/').slice(-1)[0].trim()
    if (!fileNmae) {
        ElMessage.error('未找到文件名!')
        return ''
    }
    return fileNmae
}

/**
 * 下载文件功能
 * @param url 文件下载地址
 * @param name 文件名称
 */
export const downloadFile = (
    url: string | Blob | File,
    name = '',
    suffix = ''
) => {
    if (!url) return ElMessage.error('下载地址不存在!')
    let fileName = name
    if (!name && typeof url === 'string') {
        fileName = getFileName(url)
    } else {
        fileName = `file_${new Date().getTime()}.${suffix}`
    }
    saveAs(url, fileName)
}

/**
 * 获取组件名称
 * @param route 路由信息对象
 * @returns
 */
export const getComponentName = (route: RouteLocationNormalized) => {
    // return route.matched.at(-1)?.components?.default?.__name
    return route?.name?.description
}

/**
 * 获取静态资源
 * @param fileName
 * @returns
 */
export const getAssetsFile = (fileName: string): string => {
    return new URL(`../assets/images/${fileName}`, import.meta.url).href
}

/**
 * 重新加载页面
 */
export const reloadPage = () => {
    location.reload()
}

/**
 * 获取数据类型
 */

export const getDataType = (data: any) => {
    return Object.prototype.toString.call(data)
}

// /**
//  * 字节转KB
//  * @param bytes 字节
//  * @returns KB
//  */
// export const bytesToKB = (bytes: number): number => {
//     return Math.ceil(bytes / 1024)
// }

/**
 * 获取文件大小B
 * @param file 文件
 */
export const getFileSize = (file: File | Blob): number => {
    const { size } = file
    return size
}

/**
 * 压缩文件
 * @param file 文件
 * @param quality 质量
 * @returns
 */
export const compressFun = (
    file: File | Blob,
    quality: number
): Promise<File | null> => {
    return new Promise((resolve, reject) => {
        // 压缩
        new Compressor(file, {
            quality,
            convertSize: 100,
            success(result: File) {
                resolve(result)
            },
            error(err) {
                console.error(err)
                reject(err)
            }
        })
    })
}

/**
 *
 * @param b 字节转KB
 * @returns
 */
export const kbToB = (b: number): number => {
    return b * 1024
}

/**
 * 计算放大尺寸
 * @param originalWidth 原宽
 * @param originalHeight 原高
 * @param expectWidth 最小宽
 * @param expectHeight 最小高
 * @returns
 */
export const amplificationSize = (
    originalWidth: number,
    originalHeight: number,
    expectWidth: number,
    expectHeight: number
) => {
    // 大于忽略
    if (originalWidth >= expectWidth && originalHeight >= expectHeight) {
        return {
            width: originalWidth,
            height: originalHeight
        }
    }
    // 计算宽高比例
    const widthRatio = Number((expectWidth / originalWidth).toFixed(2))
    const heightRatio = Number((expectHeight / originalHeight).toFixed(2))
    // 选择较大的比例，确保图片能够至少达到指定尺寸
    const ratio = Math.max(widthRatio, heightRatio) + 0.01
    // 计算放大后的宽度和高度
    const scaledWidth = Math.ceil(originalWidth * ratio)
    const scaledHeight = Math.ceil(originalHeight * ratio)
    // 返回放大后的尺寸
    return {
        width: scaledWidth,
        height: scaledHeight
    }
}

/**
 * 图片文件转Base64
 */
export const fileToBase64 = (file: File) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onload = function (e) {
            const base64 = e?.target?.result
            base64 ? resolve(base64) : reject(e)
        }
        reader.onerror = (err) => {
            reject(err)
        }
    })
}

/**
 *
 * @param date 格式化时间
 * @returns
 */
export const formatDate = (date: Date): string => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0') // 月份从 0 开始
    const day = String(date.getDate()).padStart(2, '0')
    const hours = String(date.getHours()).padStart(2, '0')
    const minutes = String(date.getMinutes()).padStart(2, '0')
    const seconds = String(date.getSeconds()).padStart(2, '0')
    return [year, month, day, hours, minutes, seconds].join('')
}

/**
 * 获取图片宽高
 * @param file
 */
type SizeType = {
    width: number
    height: number
}
export const getImageWH = (file: File): Promise<SizeType> => {
    return new Promise((resolve, reject) => {
        const img = new Image()
        const reader = new FileReader()

        reader.onload = function (event) {
            img.src = event?.target?.result as string
            img.onload = function () {
                const width = img.width
                const height = img.height
                const params = {
                    width,
                    height
                }
                resolve(params)
            }
            img.onerror = function (err) {
                console.error(err)
                reject(err)
            }
        }
        reader.onerror = function (err) {
            console.error(err)
            reject(err)
        }
        reader.readAsDataURL(file)
    })
}

/**
 * ip转数字
 * @param ip
 * @returns
 */
export const ipToLong = (ip: string) => {
    const parts = ip.split('.')
    return (
        (parseInt(parts[0]) << 24) |
        (parseInt(parts[1]) << 16) |
        (parseInt(parts[2]) << 8) |
        parseInt(parts[3])
    )
}

/**
 * 判断ip是否在指定ip范围内
 * @param ip
 * @param startIp
 * @param endIp
 * @returns
 */

export const isIpv4InRange = (ip: string, startIp: string, endIp: string) => {
    // 只有起始ip
    if (!endIp) {
        return ip === startIp
    }
    // ip段
    const ipNum = ipToLong(ip)
    const startIpNum = ipToLong(startIp)
    const endIpNum = ipToLong(endIp)
    return ipNum >= startIpNum && ipNum <= endIpNum
}

// 将 IPv6 地址转换为一个 128 位的 BigInt
export function ipv6ToBigInt(ip: string) {
    // 将 IPv6 地址展开为完整的 8 组
    const groups = ip.split(':')
    const fullGroups = []
    let zeroCompressionIndex = -1

    // 处理双冒号 (::) 的情况
    for (let i = 0; i < groups.length; i++) {
        if (groups[i] === '') {
            zeroCompressionIndex = i
            break
        }
    }

    if (zeroCompressionIndex !== -1) {
        const before = groups.slice(0, zeroCompressionIndex)
        const after = groups.slice(zeroCompressionIndex + 1)
        const zeroCount = 8 - (before.length + after.length)
        fullGroups.push(...before, ...Array(zeroCount).fill('0000'), ...after)
    } else {
        fullGroups.push(...groups)
    }

    // 将每组转换为 16 位整数
    const hexString = fullGroups
        .map((group) => group.padStart(4, '0')) // 每组补全到 4 位
        .join('') // 拼接成一个完整的 32 字符的十六进制字符串

    return BigInt(`0x${hexString}`) // 转换为 BigInt
}

// 判断 IPv6 地址是否在指定范围内
export function isIpv6InRange(ip: string, startIp: string, endIp: string) {
    const ipInt = ipv6ToBigInt(ip)
    const startIpInt = ipv6ToBigInt(startIp)
    const endIpInt = ipv6ToBigInt(endIp)

    return ipInt >= startIpInt && ipInt <= endIpInt
}

// 判断Ip是否在指定范围
export const isIpRange = (
    ip: string,
    startIp: string,
    endIp: string,
    ipV6 = false
) => {
    return ipV6
        ? isIpv6InRange(ip, startIp, endIp)
        : isIpv4InRange(ip, startIp, endIp)
}

// 字符串转数组
export const strToArray = (str: string | null) => {
    const strContent = str?.trim()
    if (!strContent) return []
    return strContent.split(',').filter((item) => item.trim())
}

// 重复值判断
export const hasDuplicates = (arr: any[]) => {
    return new Set(arr).size !== arr.length
}

// 获取数组中重复的值
export const getDuplicates = (arr: any[]) => {
    const seen = new Set()
    const duplicates = new Set()

    arr.forEach((item) => {
        if (seen.has(item)) {
            duplicates.add(item)
        } else {
            seen.add(item)
        }
    })

    return Array.from(duplicates)
}

// 提取省市区
export const extractAddress = (address: string): Promise<string[]> => {
    const params = {
        address
    }
    return new Promise((resolve, reject) => {
        smart(params)
            .then((data) => {
                const { province, city, region } = data
                if (province && city && region) {
                    resolve([province, city, region])
                    return
                }
                resolve([])
            })
            .catch((err) => {
                resolve([])
            })
    })
}

// 查询省市区Code
export const extractCode = (
    address: string[],
    addressList: Array<{
        name: string
        value: number
        child?: Array<{
            name: string
            value: number
            child?: Array<{ name: string; value: number }>
        }>
    }>
): number[] => {
    if (!address.length || address.length < 3) {
        return []
    }

    const [provinceName, cityName, countyName] = address

    // 查找省份
    const province = addressList.find((item) => item.name === provinceName)
    if (!province) return []

    const provinceCode = province.value

    // 查找城市
    const city = province.child?.find((item) => item.name === cityName)
    if (!city) return []

    const cityCode = city.value

    // 查找区县
    const county = city.child?.find((item) => item.name === countyName)
    if (!county) return []

    const countyCode = county.value
    return [provinceCode, cityCode, countyCode]
}

// 特殊地址处理
export const specialAddress = (parseAddr: string[]) => {
    let [province, city, region] = parseAddr
    if (province == '北京' && city == '北京市') {
        province = '北京市'
        city = '市辖区'
    }
    if (province == '天津' && city == '天津市') {
        province = '天津市'
        city = '市辖区'
    }
    if (province == '上海' && city == '上海市') {
        province = '上海市'
        city = '市辖区'
    }
    if (province == '重庆' && city == '重庆市') {
        province = '重庆市'
        city = '市辖区'
    }
    return [province, city, region]
}

// 地区提取Code
export const addressToAddressCode = async (
    address: string,
    addressList: Array<any>
) => {
    const addr = address.replace(/\s+/g, '')
    let parseAddr = await extractAddress(addr)
    parseAddr = specialAddress(parseAddr)
    const result = extractCode(parseAddr, addressList)
    return result
}
